/*
<summary>
   This functional component renders the inline filter bar shared by the Crimping Inspection
   Record List and User Created Inspection Record List modules (standalone routes and, more
   commonly, the Equipment Inspection Menu tabs that embed them), styled like the Tool
   Management module's inline filter row.
   Developer: Aashish Singh, Created Date: 01-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Formik, Form as FormikForm } from 'formik';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { DateFormatNew } from '../../constants/date-picker-constant';
import { SharedInspectionFilterInitialState, ISharedInspectionFilter } from '../../models/ISharedInspectionFilter';
import { IOption } from '../../models/ICommon';
import FormikFormDatePicker from '../FormikFormHandlers/FormikFormDatePicker';
import FormikFormInput from '../FormikFormHandlers/FormikFormInput';
import FormikFormSelect from '../FormikFormHandlers/FormikFormSelect';
import { ToolFilterValidationSchema } from '../FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../FormLoader/FormLoader';
import Label from '../label/label';
import { formatMessage } from '../../translations/format-message';

const JudgmentResultOptions: IOption[] = [
  { id: 0, value: '', disabled: false },
  { id: 1, value: 'OK', disabled: false },
  { id: 2, value: 'NG', disabled: false },
];

const JudgmentResultLabels: Record<string, string> = {
  '': formatMessage('all'),
  OK: 'OK',
  NG: formatMessage('ng'),
};

interface IProps {
  initialValues: ISharedInspectionFilter;
  submitHandler: (data: ISharedInspectionFilter) => void;
  filterParmas: (data: ISharedInspectionFilter, isfilterCleard: boolean) => void;
  isLoading: boolean;
  isFilterCleared: boolean;
  factoryOptions: IOption[];
}

/**
* This functional component renders the shared inline filter bar and hands the
* applied/cleared filter values back to the caller.
* @param props: Initial filter values, submit/clear handlers and factory options
*/
const InspectionFilter = (props: IProps) => {
  function getLocalISODate(isoDate: Date) {
    return (
      isoDate.getFullYear() +
      '-' +
      String(isoDate.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(isoDate.getDate()).padStart(2, '0')
    );
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={props.initialValues}
        enableReinitialize
        validateOnBlur={false}
        validationSchema={ToolFilterValidationSchema}
        onSubmit={async (values: ISharedInspectionFilter) => {
          if (values.InspectionDateFrom) {
            values = {
              ...values,
              InspectionDateFrom: getLocalISODate(new Date(values.InspectionDateFrom)),
            };
          }
          if (values.InspectionDateTo) {
            values = {
              ...values,
              InspectionDateTo: getLocalISODate(new Date(values.InspectionDateTo)),
            };
          }
          props.submitHandler(values);
        }}
      >
        {({ resetForm, values }) => (
          <FormikForm className="user-form pt-2 px-4">
            {props.isLoading && <FormLoader loading={props.isLoading} />}
            <Row className="g-2">
              <Col>
                <Label
                  className="label-style col-form-label text-truncate d-block"
                  required={false}
                  label="owner_factory"
                  htmlFor="OwnerFactoryId"
                />
                <Field
                  data-testid="OwnerFactoryId"
                  name="OwnerFactoryId"
                  as={FormikFormSelect}
                  options={props.factoryOptions}
                  className="form-control"
                />
              </Col>
              <Col>
                <Label
                  className="label-style col-form-label text-truncate d-block"
                  required={false}
                  label="tool_management_no"
                  htmlFor="ToolManagementNo"
                />
                <Field
                  data-testid="ToolManagementNo"
                  className="form-control"
                  name="ToolManagementNo"
                  autoComplete="off"
                  placeholder="tool_management_no_placeholder"
                  as={FormikFormInput}
                />
              </Col>
              <Col>
                <Label
                  className="label-style col-form-label text-truncate d-block"
                  required={false}
                  label="tool_name"
                  htmlFor="ToolName"
                />
                <Field
                  data-testid="ToolName"
                  className="form-control"
                  name="ToolName"
                  autoComplete="off"
                  placeholder="tool_name_placeholder"
                  as={FormikFormInput}
                />
              </Col>
              <Col>
                <Label
                  className="label-style col-form-label text-truncate d-block"
                  required={false}
                  label="inspection_date_from"
                  htmlFor="InspectionDateFrom"
                />
                <Field
                  data-testid="InspectionDateFrom"
                  className="form-control"
                  name="InspectionDateFrom"
                  autoComplete="off"
                  dateFormat={DateFormatNew}
                  as={FormikFormDatePicker}
                  isClearable={values.InspectionDateFrom}
                  maxDate={values.InspectionDateTo ? new Date(values.InspectionDateTo) : undefined}
                />
              </Col>
              <Col>
                <Label
                  className="label-style col-form-label text-truncate d-block"
                  required={false}
                  label="inspection_date_to"
                  htmlFor="InspectionDateTo"
                />
                <Field
                  data-testid="InspectionDateTo"
                  className="form-control"
                  name="InspectionDateTo"
                  autoComplete="off"
                  dateFormat={DateFormatNew}
                  as={FormikFormDatePicker}
                  isClearable={values.InspectionDateTo}
                  minDate={values.InspectionDateFrom ? new Date(values.InspectionDateFrom) : undefined}
                />
              </Col>
              <Col>
                <Label
                  className="label-style col-form-label text-truncate d-block"
                  required={false}
                  label="judgment_result"
                  htmlFor="JudgmentResult"
                />
                <Field
                  data-testid="JudgmentResult"
                  name="JudgmentResult"
                  as={FormikFormSelect}
                  options={JudgmentResultOptions}
                  labels={JudgmentResultLabels}
                  getData={true}
                  className="form-control"
                />
              </Col>
              <Col
                className="d-flex gap-2 justify-content-end clear-filter-btn bom-serch-btn align-items-end"
              >
                <Button
                  variant="secondary"
                  type="submit"
                >
                  {formatMessage('search')}
                </Button>
                <Button
                  className="text-white bom-clear-btn"
                  disabled={props.isFilterCleared}
                  variant="danger"
                  onClick={() => {
                    props.filterParmas(SharedInspectionFilterInitialState, true);
                    resetForm();
                  }}
                >
                  {formatMessage('clear')}
                </Button>
              </Col>
            </Row>
          </FormikForm>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default InspectionFilter;
